/**
 * v109.9.116 — TicketBookingBell
 * ------------------------------
 * A top-bar icon that surfaces every Travel Booking currently ready to be
 * ticketed (status=Approved). Visible only to users with ticket-booking
 * access (i_can_book) — silently hidden for everyone else.
 *
 * Design:
 *   • Polls `/travel-bookings/access` on mount for gating.
 *   • Loads `/travel-bookings?scope=booking` when opened → filters status=Approved.
 *   • Clicking a row opens the shared ViewDialog (from TravelBooking.jsx) with
 *     the ticket-booking edit UI already visible; the same dialog handles
 *     "Save Details" + "Mark Booked".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Ticket, Plane } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "./ui/dropdown-menu";
import { ViewDialog as TravelBookingDialog } from "../pages/TravelBooking";

function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 16);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

export default function TicketBookingBell() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const pollRef = useRef(null);

  const loadAccess = useCallback(async () => {
    if (!user?.id) { setVisible(false); return; }
    try {
      const { data } = await api.get("/travel-bookings/tab-access");
      // Master Admin OR any user granted the Ticket Booking Details tab.
      setVisible(!!(data?.is_master || data?.i_can_book));
    } catch { setVisible(false); }
  }, [user?.id]);

  const loadCount = useCallback(async () => {
    if (!visible) { setCount(0); return; }
    try {
      const { data } = await api.get("/travel-bookings?scope=booking");
      const approved = (Array.isArray(data) ? data : []).filter((r) => r.status === "Approved");
      setCount(approved.length);
    } catch { setCount(0); }
  }, [visible]);

  const loadRows = useCallback(async () => {
    try {
      const { data } = await api.get("/travel-bookings?scope=booking");
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => {
    if (!visible) return;
    loadCount();
    pollRef.current = setInterval(loadCount, 60000);
    return () => clearInterval(pollRef.current);
  }, [visible, loadCount]);
  useEffect(() => { if (open) loadRows(); }, [open, loadRows]);

  if (!visible) return null;

  const approved = rows.filter((r) => r.status === "Approved");
  const booked = rows.filter((r) => r.status === "Booked");

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="relative h-9 w-9 p-0 rounded-full bg-white/70 dark:bg-slate-800/70 backdrop-blur border border-slate-200 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-blue-500/15 hover:border-blue-300 dark:hover:border-blue-500/40 active:scale-95 hover:-translate-y-0.5 hover:shadow-md hover:shadow-blue-200/40 dark:hover:shadow-blue-500/20 transition-all duration-150"
            data-testid="ticket-booking-bell"
            title="Approved bookings ready to ticket"
          >
            <Ticket size={16} className="text-blue-700 dark:text-blue-400" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-900" data-testid="ticket-booking-bell-count">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[440px] max-h-[70vh] overflow-y-auto p-0" data-testid="ticket-booking-bell-panel">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[13px] text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                <Ticket size={13} className="text-blue-700" /> Ticket Booking Queue
              </div>
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">{approved.length} to book</Badge>
            </div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">
              Click a row to review details, fill PNR / vendor / cost, then Mark Booked.
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-[12px]">
              No approved bookings awaiting ticketing.
            </div>
          ) : (
            <div>
              {approved.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60 dark:bg-slate-800/40">
                    To Be Ticketed ({approved.length})
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {approved.map((r) => <BookingRow key={r.id} r={r} onOpen={(b) => { setOpen(false); setBanner(b); }} />)}
                  </div>
                </div>
              )}
              {booked.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60 dark:bg-slate-800/40 border-t border-slate-200 dark:border-slate-700">
                    Recently Booked — edit here ({booked.length})
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {booked.slice(0, 15).map((r) => <BookingRow key={r.id} r={r} onOpen={(b) => { setOpen(false); setBanner(b); }} booked />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {banner && (
        <TravelBookingDialog
          viewing={banner}
          setViewing={setBanner}
          access={{ i_can_approve: false, i_can_book: true, i_can_reimburse: false, is_master: user?.role === "master_admin", approval_user_ids: [], ticket_user_ids: [] }}
          onChanged={() => { loadCount(); loadRows(); }}
        />
      )}
    </>
  );
}

function BookingRow({ r, onOpen, booked = false }) {
  return (
    <button
      onClick={() => onOpen(r)}
      className="w-full text-left px-3 py-2.5 hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors"
      data-testid={`ticket-booking-bell-row-${r.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Plane size={11} className="text-blue-600 shrink-0" />
            <span className="font-semibold text-[12.5px] text-slate-900 dark:text-slate-100 truncate">{r.traveler_full_name || r.created_by_name}</span>
            <Badge className={`text-[9px] ${booked ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-blue-100 text-blue-800 border-blue-200"}`}>
              {r.status}
            </Badge>
          </div>
          <div className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-1 mt-0.5">
            {r.travel_type} · {r.mode_of_travel} · {r.from_city || (r.cities?.[0]) || "—"} → {r.to_city || (r.cities?.slice(-1)?.[0]) || "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Requested by <b>{r.created_by_name}</b> · Preferred {r.preferred_travel_date || "—"}
          </div>
          {r.approved_at && <div className="text-[10px] text-slate-500">Approved: {fmtWhen(r.approved_at)}</div>}
          {r.booked_at && <div className="text-[10px] text-emerald-700 dark:text-emerald-400">Booked: {fmtWhen(r.booked_at)} by {r.booked_by_name || "—"}</div>}
        </div>
      </div>
    </button>
  );
}
